/**
 * Report Export Utilities
 * Provides PDF and Excel export functionality for all report types
 */

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { createDevConsole } from "./devLogger";

const console = createDevConsole();

// ============================================================================
// PDF Export Functions
// ============================================================================

/**
 * Create a base PDF document with standard styling
 * @param {string} orientation - "portrait" or "landscape"
 * @param {string} format - "a4", "a3", "a2", "letter", etc.
 */
const createPDFDocument = (orientation = "landscape", format = "a4") => {
  const doc = new jsPDF({
    orientation,
    unit: "mm",
    format: format,
  });

  // Set default font
  doc.setFont("helvetica");

  return doc;
};

/**
 * Add a title header to the PDF
 */
const addPDFHeader = (doc, title, subtitle = null, generatedAt = null) => {
  let yPos = 15;

  // Title
  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text(title, 14, yPos);
  yPos += 8;

  // Subtitle
  if (subtitle) {
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text(subtitle, 14, yPos);
    yPos += 6;
  }

  // Generated date
  if (generatedAt) {
    doc.setFontSize(9);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date(generatedAt).toLocaleString()}`, 14, yPos);
    yPos += 4;
  }

  // Export date
  doc.setFontSize(9);
  doc.setTextColor(128, 128, 128);
  doc.text(`Exported: ${new Date().toLocaleString()}`, 14, yPos);

  return yPos + 8;
};

/**
 * Add a section header
 */
const addSectionHeader = (doc, title, yPos) => {
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.text(title, 14, yPos);
  doc.setFont("helvetica", "normal");
  return yPos + 6;
};

/**
 * Get lifecycle status color for PDF
 */
const getLifecycleStatusColor = (status) => {
  const colors = {
    Planned: [59, 130, 246], // blue
    "Phase In": [234, 179, 8], // yellow
    Active: [34, 197, 94], // green
    "Phase Out": [249, 115, 22], // orange
    "End of Life": [239, 68, 68], // red
    Unknown: [107, 114, 128], // gray
  };
  return colors[status] || colors["Unknown"];
};

/**
 * Export Roadmap Report to PDF with Visual Timeline
 * Creates a visual representation similar to the UI with swimlanes and colored timeline bars
 */
export const exportRoadmapToPDF = (report, data, _options = {}) => {
  const rows = data?.data?.rows || [];
  const clusterBy = data?.cluster_by;
  const totalEntities = data?.data?.total_entities || 0;

  // Calculate timeline intervals first to determine page size
  const { minDate, maxDate } = calculateDateRange(rows);
  const timelineStart = minDate || new Date();
  const timelineEnd =
    maxDate || new Date(timelineStart.getTime() + 365 * 24 * 60 * 60 * 1000);
  const intervals = generateTimelineIntervals(timelineStart, timelineEnd);

  // Determine page format based on number of intervals
  // A4 Landscape: 297mm wide, A3 Landscape: 420mm wide
  const minIntervalWidth = 18; // Minimum width per interval for readability
  const entityColumnWidth = 55;
  const margin = 10;
  const requiredTimelineWidth = intervals.length * minIntervalWidth;
  const requiredTotalWidth =
    margin * 2 + entityColumnWidth + 5 + requiredTimelineWidth;

  // Use A3 if we need more than A4 landscape width (297mm), or A2 for very wide reports
  let pageFormat = "a4";
  if (requiredTotalWidth > 290) {
    pageFormat = "a3";
  }
  if (requiredTotalWidth > 410) {
    pageFormat = "a2";
  }

  const doc = createPDFDocument("landscape", pageFormat);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Layout constants - adjust based on available space
  const timelineStartX = margin + entityColumnWidth + 5;
  const timelineWidth = pageWidth - timelineStartX - margin;
  const rowHeight = 8;
  const groupRowHeight = 7;
  const headerHeight = 12;

  // Header
  let yPos = 12;
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.text(report?.name || "Roadmap Report", margin, yPos);

  yPos += 5;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);
  doc.text(
    `Entity Type: ${data?.entity_type || "application"} • ${totalEntities} entities${clusterBy ? ` • Grouped by: ${clusterBy.replace(/-/g, " ")}` : ""} • Exported: ${new Date().toLocaleString()}`,
    margin,
    yPos
  );

  // Legend
  yPos += 6;
  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.text("Legend:", margin, yPos);

  const legendItems = [
    { status: "Planned", color: [59, 130, 246] },
    { status: "Phase In", color: [234, 179, 8] },
    { status: "Active", color: [34, 197, 94] },
    { status: "Phase Out", color: [249, 115, 22] },
    { status: "End of Life", color: [239, 68, 68] },
  ];

  let legendX = margin + 18;
  legendItems.forEach(({ status, color }) => {
    doc.setFillColor(...color);
    doc.rect(legendX, yPos - 2.5, 6, 3, "F");
    doc.setTextColor(60, 60, 60);
    doc.text(status, legendX + 7, yPos);
    legendX += 32;
  });

  yPos += 8;

  // Timeline header background
  doc.setFillColor(31, 41, 55);
  doc.rect(margin, yPos, pageWidth - margin * 2, headerHeight, "F");

  // Entity column header
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.text("Entity / Group", margin + 2, yPos + 7);

  // Draw timeline interval headers
  const intervalWidth = timelineWidth / intervals.length;
  intervals.forEach((interval, idx) => {
    const x = timelineStartX + idx * intervalWidth;
    doc.setDrawColor(55, 65, 81);
    doc.line(x, yPos, x, yPos + headerHeight);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.text(interval.label, x + 2, yPos + 7);
  });

  yPos += headerHeight;
  // Draw rows
  let currentY = yPos;

  rows.forEach((row, rowIdx) => {
    // Check if we need a new page
    if (
      currentY + (row._isGroup ? groupRowHeight : rowHeight) >
      pageHeight - 15
    ) {
      addPageNumbers(doc);
      doc.addPage();
      currentY = margin;

      // Redraw header on new page
      doc.setFillColor(31, 41, 55);
      doc.rect(margin, currentY, pageWidth - margin * 2, headerHeight, "F");
      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.text("Entity / Group", margin + 2, currentY + 7);

      intervals.forEach((interval, idx) => {
        const x = timelineStartX + idx * intervalWidth;
        doc.setDrawColor(55, 65, 81);
        doc.line(x, currentY, x, currentY + headerHeight);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(7);
        doc.text(interval.label, x + 2, currentY + 7);
      });

      currentY += headerHeight;
    }

    if (row._isGroup) {
      // Swimlane header
      doc.setFillColor(55, 65, 81);
      doc.rect(margin, currentY, pageWidth - margin * 2, groupRowHeight, "F");

      doc.setFontSize(8);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      const indent = row.level ? "  ".repeat(row.level) : "";
      const groupText = `${indent}${row.groupName}${row.entityCount !== undefined ? ` (${row.entityCount})` : ""}`;
      doc.text(groupText, margin + 2, currentY + 5);

      currentY += groupRowHeight;
    } else {
      // Entity row - alternating background
      if (rowIdx % 2 === 0) {
        doc.setFillColor(249, 250, 251);
        doc.rect(margin, currentY, pageWidth - margin * 2, rowHeight, "F");
      }

      // Draw grid lines
      doc.setDrawColor(229, 231, 235);
      doc.line(
        margin,
        currentY + rowHeight,
        pageWidth - margin,
        currentY + rowHeight
      );

      // Entity name
      doc.setFontSize(7);
      doc.setTextColor(30, 30, 30);
      doc.setFont("helvetica", "normal");
      const entityName =
        (row.name || "").substring(0, 30) +
        (row.name?.length > 30 ? "..." : "");
      doc.text(entityName, margin + 2, currentY + 5);

      // Current status badge
      const statusColor = getLifecycleStatusColor(row.lifecycle_status);
      if (statusColor) {
        doc.setFillColor(...statusColor);
        const statusWidth = doc.getTextWidth(row.lifecycle_status || "") + 3;
        doc.roundedRect(
          margin + entityColumnWidth - statusWidth - 2,
          currentY + 1.5,
          statusWidth,
          5,
          1,
          1,
          "F"
        );
        doc.setFontSize(5);
        doc.setTextColor(255, 255, 255);
        doc.text(
          row.lifecycle_status || "",
          margin + entityColumnWidth - statusWidth,
          currentY + 5
        );
      }

      // Draw timeline bars for this entity
      drawEntityTimeline(
        doc,
        row,
        timelineStartX,
        currentY,
        timelineWidth,
        rowHeight - 2,
        timelineStart,
        timelineEnd
      );

      // Draw vertical interval lines
      doc.setDrawColor(229, 231, 235);
      intervals.forEach((interval, idx) => {
        const x = timelineStartX + idx * intervalWidth;
        doc.line(x, currentY, x, currentY + rowHeight);
      });

      currentY += rowHeight;
    }
  });

  // Handle empty data
  if (rows.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text(
      "No data to export (check your filters)",
      pageWidth / 2 - 30,
      currentY + 20
    );
  }

  // Add page numbers
  addPageNumbers(doc);

  // Save
  const filename = sanitizeFilename(report?.name || "roadmap_report") + ".pdf";
  doc.save(filename);
};

/**
 * Calculate min and max dates from the rows
 * Excludes past years - minimum date is current year
 */
const calculateDateRange = (rows) => {
  let minDate = null;
  let maxDate = null;
  const currentYear = new Date().getFullYear();

  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      if (typeof dateValue === "string") {
        const dateStr = dateValue.split("T")[0];
        return new Date(dateStr);
      }
      return new Date(dateValue);
    } catch {
      return null;
    }
  };

  rows.forEach((row) => {
    if (row._isGroup) return;

    const attrs = row.timeline_attributes || {};
    const dates = [
      parseDate(attrs.plan_start_date),
      parseDate(attrs.phase_in_start_date),
      parseDate(attrs.active_start_date),
      parseDate(attrs.phase_out_start_date),
      parseDate(attrs.eol_start_date),
    ].filter(Boolean);

    dates.forEach((date) => {
      // Only consider dates from current year onwards for min date
      if (date.getFullYear() >= currentYear) {
        if (!minDate || date < minDate) minDate = date;
      }
      // For max date, consider all future dates
      if (!maxDate || date > maxDate) maxDate = date;
    });
  });

  // Ensure minimum date starts from current year
  if (!minDate || minDate.getFullYear() < currentYear) {
    minDate = new Date(currentYear, 0, 1); // Start of current year
  } else {
    minDate = new Date(minDate.getFullYear(), 0, 1); // Start of that year
  }

  // Ensure we have at least 2 years of timeline
  if (!maxDate || maxDate.getFullYear() < currentYear + 1) {
    maxDate = new Date(currentYear + 2, 11, 31); // End of year after next
  } else {
    maxDate = new Date(maxDate.getFullYear() + 1, 11, 31); // End of next year after max
  }

  return { minDate, maxDate };
};

/**
 * Generate timeline intervals (years/quarters)
 * Excludes past years - starts from current year
 */
const generateTimelineIntervals = (startDate, endDate) => {
  const intervals = [];
  const currentYear = new Date().getFullYear();
  const currentQuarter = Math.floor(new Date().getMonth() / 3);

  // Adjust start date to not go before current year
  const dataStart = new Date(startDate);
  const effectiveStartYear = Math.max(dataStart.getFullYear(), currentYear);
  const start = new Date(effectiveStartYear, 0, 1);

  const end = new Date(endDate);

  // If end date is before current year, extend to at least current year + 1
  if (end.getFullYear() < currentYear) {
    end.setFullYear(currentYear + 1);
  }

  // Calculate total months from effective start
  const totalMonths =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth());

  if (totalMonths > 36) {
    // Use years - start from current year
    for (let year = effectiveStartYear; year <= end.getFullYear(); year++) {
      intervals.push({
        label: String(year),
        start: new Date(year, 0, 1),
        end: new Date(year, 11, 31),
      });
    }
  } else {
    // Use quarters - start from current quarter
    let current = new Date(effectiveStartYear, currentQuarter * 3, 1);
    while (current <= end) {
      const quarter = Math.floor(current.getMonth() / 3) + 1;
      intervals.push({
        label: `Q${quarter} ${current.getFullYear()}`,
        start: new Date(current),
        end: new Date(current.getFullYear(), current.getMonth() + 3, 0),
      });
      current.setMonth(current.getMonth() + 3);
    }
  }

  return intervals;
};

/**
 * Draw timeline bars for an entity
 */
const drawEntityTimeline = (
  doc,
  row,
  startX,
  startY,
  width,
  height,
  timelineStart,
  timelineEnd
) => {
  const attrs = row.timeline_attributes || {};
  const timelineRange = timelineEnd.getTime() - timelineStart.getTime();

  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      if (typeof dateValue === "string") {
        const dateStr = dateValue.split("T")[0];
        return new Date(dateStr);
      }
      return new Date(dateValue);
    } catch {
      return null;
    }
  };

  // Define lifecycle phases in order
  const phases = [
    {
      key: "plan_start_date",
      nextKey: "phase_in_start_date",
      status: "Planned",
      color: [59, 130, 246],
    },
    {
      key: "phase_in_start_date",
      nextKey: "active_start_date",
      status: "Phase In",
      color: [234, 179, 8],
    },
    {
      key: "active_start_date",
      nextKey: "phase_out_start_date",
      status: "Active",
      color: [34, 197, 94],
    },
    {
      key: "phase_out_start_date",
      nextKey: "eol_start_date",
      status: "Phase Out",
      color: [249, 115, 22],
    },
    {
      key: "eol_start_date",
      nextKey: null,
      status: "End of Life",
      color: [239, 68, 68],
    },
  ];

  phases.forEach((phase) => {
    const phaseStart = parseDate(attrs[phase.key]);
    if (!phaseStart) return;

    // Find the end of this phase (start of next phase, or timeline end)
    let phaseEnd = null;
    if (phase.nextKey) {
      phaseEnd = parseDate(attrs[phase.nextKey]);
    }
    if (!phaseEnd) {
      // Extend to 2 years after start or timeline end
      phaseEnd = new Date(
        Math.min(
          phaseStart.getTime() + 2 * 365 * 24 * 60 * 60 * 1000,
          timelineEnd.getTime()
        )
      );
    }

    // Calculate positions
    const x1 =
      startX +
      ((phaseStart.getTime() - timelineStart.getTime()) / timelineRange) *
        width;
    const x2 =
      startX +
      ((phaseEnd.getTime() - timelineStart.getTime()) / timelineRange) * width;

    // Clamp to visible range
    const visibleX1 = Math.max(x1, startX);
    const visibleX2 = Math.min(x2, startX + width);

    if (visibleX2 > visibleX1) {
      doc.setFillColor(...phase.color);
      doc.rect(visibleX1, startY + 1, visibleX2 - visibleX1, height, "F");
    }
  });
};

/**
 * Export Data Quality Report to PDF
 */
const exportDataQualityToPDF = (report, data, _options = {}) => {
  const doc = createPDFDocument("landscape");

  const summary = data?.summary || {};

  // Header
  let yPos = addPDFHeader(
    doc,
    report?.name || "Data Quality Report",
    `Entity Type: ${data?.entity_type || "unknown"} • ${summary.total_entities || 0} entities`,
    data?.generated_at
  );

  // Summary section
  yPos = addSectionHeader(doc, "Summary", yPos);

  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);

  const summaryText = [
    `Total Entities: ${summary.total_entities || 0}`,
    `Complete: ${summary.complete_entities || 0} (${summary.total_entities > 0 ? Math.round((summary.complete_entities / summary.total_entities) * 100) : 0}%)`,
    `Incomplete: ${summary.incomplete_entities || 0}`,
    `Missing Required: ${summary.issues_by_type?.missing_required || 0}`,
    `Missing Optional: ${summary.issues_by_type?.missing_optional || 0}`,
  ];

  summaryText.forEach((text, idx) => {
    doc.text(text, 14 + (idx % 3) * 70, yPos + Math.floor(idx / 3) * 5);
  });

  yPos += 15;

  // Attribute Completeness
  if (Object.keys(summary.attribute_completeness || {}).length > 0) {
    yPos = addSectionHeader(doc, "Attribute Completeness", yPos);

    const attrData = Object.entries(summary.attribute_completeness).map(
      ([attr, stats]) => [
        attr.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        `${stats.percentage}%`,
        `${stats.filled} / ${stats.filled + stats.empty}`,
      ]
    );

    autoTable(doc, {
      startY: yPos,
      head: [["Attribute", "Completeness", "Filled / Total"]],
      body: attrData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [31, 41, 55], textColor: [255, 255, 255] },
      margin: { left: 14 },
      tableWidth: 120,
    });

    yPos = doc.lastAutoTable.finalY + 10;
  }

  // Entity Details
  yPos = addSectionHeader(doc, "Entity Details", yPos);

  const rows = data?.data?.rows || [];
  const columns = data?.columns || [];

  // Build dynamic columns
  const tableHead = ["Entity Name", "Quality Score", "Issues"];
  columns.forEach((col) => tableHead.push(col.label));

  const tableData = rows.map((row) => {
    const rowData = [
      row.name || "",
      `${row.quality_score || 0}%`,
      row.issues?.length > 0
        ? row.issues.map((i) => i.attribute).join(", ")
        : "None",
    ];

    columns.forEach((col) => {
      const value =
        col.type === "custom"
          ? row.custom_attributes?.[col.name]
          : row.attributes?.[col.name];
      rowData.push(value || "—");
    });

    return rowData;
  });

  autoTable(doc, {
    startY: yPos,
    head: [tableHead],
    body: tableData,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: {
      fillColor: [31, 41, 55],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 20 },
      2: { cellWidth: 40 },
    },
    didParseCell: (data) => {
      if (data.column.index === 1 && data.section === "body") {
        const score = parseInt(data.cell.raw);
        if (score >= 90) data.cell.styles.textColor = [34, 197, 94];
        else if (score >= 70) data.cell.styles.textColor = [234, 179, 8];
        else if (score >= 50) data.cell.styles.textColor = [249, 115, 22];
        else data.cell.styles.textColor = [239, 68, 68];
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  addPageNumbers(doc);

  const filename =
    sanitizeFilename(report?.name || "data_quality_report") + ".pdf";
  doc.save(filename);
};

/**
 * Export Relationship Report to PDF
 */
const exportRelationshipToPDF = (report, data, _options = {}) => {
  const doc = createPDFDocument("landscape");

  const summary = data?.summary || {};
  const variant = data?.variant || "impact-analysis";

  // Header
  let yPos = addPDFHeader(
    doc,
    report?.name || "Relationship Report",
    `Variant: ${getVariantTitle(variant)} • ${summary.total_source_entities || 0} source entities`,
    data?.generated_at
  );

  // Summary section
  yPos = addSectionHeader(doc, "Summary", yPos);

  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);

  if (variant === "impact-analysis" || variant === "dependency") {
    doc.text(
      `Source Entities: ${summary.total_source_entities || 0}`,
      14,
      yPos
    );
    doc.text(`Total Impacted: ${summary.total_impacted || 0}`, 80, yPos);
    doc.text(`Analysis Depth: ${data?.analysis_depth || 2} hops`, 140, yPos);
    doc.text(`Direction: ${data?.analysis_direction || "both"}`, 200, yPos);
    yPos += 8;

    // Impacts by type
    if (Object.keys(summary.impacted_by_type || {}).length > 0) {
      doc.text(
        "Impacts by Type: " +
          Object.entries(summary.impacted_by_type)
            .map(([type, count]) => `${type}: ${count}`)
            .join(", "),
        14,
        yPos
      );
      yPos += 8;
    }
  } else if (variant === "matrix") {
    doc.text(`Source Count: ${summary.source_count || 0}`, 14, yPos);
    doc.text(`Target Count: ${summary.target_count || 0}`, 80, yPos);
    doc.text(`Connected: ${summary.connected_cells || 0}`, 140, yPos);
    doc.text(`Coverage: ${summary.coverage_percentage || 0}%`, 200, yPos);
    yPos += 8;
  }

  yPos += 4;

  // Details based on variant
  if (variant === "impact-analysis" || variant === "dependency") {
    yPos = addSectionHeader(doc, "Impact Analysis Details", yPos);

    const results = data?.data?.results || [];
    const tableData = [];

    results.forEach((result) => {
      if (result.impacts && result.impacts.length > 0) {
        result.impacts.forEach((impact, idx) => {
          tableData.push([
            idx === 0 ? result.source.name : "",
            idx === 0 ? result.source.type : "",
            impact.name,
            impact.type,
            impact.depth,
            impact.direction,
            impact.relationship_type,
          ]);
        });
      } else {
        tableData.push([
          result.source.name,
          result.source.type,
          "No impacts",
          "",
          "",
          "",
          "",
        ]);
      }
    });

    autoTable(doc, {
      startY: yPos,
      head: [
        [
          "Source",
          "Type",
          "Impacted Entity",
          "Type",
          "Depth",
          "Direction",
          "Relationship",
        ],
      ],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [31, 41, 55], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [249, 250, 251] },
    });
  } else if (variant === "matrix") {
    yPos = addSectionHeader(doc, "Relationship Matrix", yPos);

    const { sources, targets, matrix } = data?.data || {};

    if (sources && targets && matrix) {
      const tableHead = [
        "Source / Target",
        ...targets.map((t) => t.name.substring(0, 15)),
      ];
      const tableData = sources.map((source) => {
        const row = [source.name];
        targets.forEach((target) => {
          const cell = matrix[source.id]?.connections[target.id];
          row.push(cell?.connected ? (cell.count > 1 ? cell.count : "✓") : "-");
        });
        return row;
      });

      autoTable(doc, {
        startY: yPos,
        head: [tableHead],
        body: tableData,
        styles: { fontSize: 7, cellPadding: 1 },
        headStyles: { fillColor: [31, 41, 55], textColor: [255, 255, 255] },
      });
    }
  }

  addPageNumbers(doc);

  const filename =
    sanitizeFilename(report?.name || "relationship_report") + ".pdf";
  doc.save(filename);
};

// ============================================================================
// Excel Export Functions
// ============================================================================

/**
 * Export Roadmap Report to Excel with Swimlanes
 * Creates a clear representation with swimlane groupings and lifecycle data
 */
export const exportRoadmapToExcel = (report, data, _options = {}) => {
  const wb = XLSX.utils.book_new();

  const rows = data?.data?.rows || [];
  const clusterBy = data?.cluster_by;
  const totalEntities = data?.data?.total_entities || 0;

  // Track groups for summary
  const groupCounts = {};
  const statusCounts = {};

  // ==========================================
  // SHEET 1: Roadmap with Swimlanes
  // ==========================================
  const roadmapData = [];

  // Header row
  roadmapData.push([
    "SWIMLANE / ENTITY",
    "LIFECYCLE STATUS",
    "PLAN START",
    "PHASE IN",
    "ACTIVE",
    "PHASE OUT",
    "END OF LIFE",
    "SUCCESSORS",
  ]);

  rows.forEach((row) => {
    if (row._isGroup) {
      // SWIMLANE HEADER ROW - make it prominent
      const swimlaneLabel = `═══ ${row.groupName.toUpperCase()} (${row.entityCount || 0} entities) ═══`;
      roadmapData.push([swimlaneLabel, "", "", "", "", "", "", ""]);
      groupCounts[row.groupName] = row.entityCount || 0;
    } else {
      // Entity row - indented under swimlane
      const attrs = row.timeline_attributes || {};
      roadmapData.push([
        "    " + (row.name || ""),
        row.lifecycle_status || "Unknown",
        formatDateForExport(attrs.plan_start_date),
        formatDateForExport(attrs.phase_in_start_date),
        formatDateForExport(attrs.active_start_date),
        formatDateForExport(attrs.phase_out_start_date),
        formatDateForExport(attrs.eol_start_date),
        row.successors?.map((s) => s.name).join(", ") || "",
      ]);

      // Track status
      const status = row.lifecycle_status || "Unknown";
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    }
  });

  const roadmapWs = XLSX.utils.aoa_to_sheet(roadmapData);
  roadmapWs["!cols"] = [
    { wch: 50 }, // Swimlane/Entity
    { wch: 15 }, // Status
    { wch: 12 }, // Plan Start
    { wch: 12 }, // Phase In
    { wch: 12 }, // Active
    { wch: 12 }, // Phase Out
    { wch: 12 }, // EOL
    { wch: 35 }, // Successors
  ];
  XLSX.utils.book_append_sheet(wb, roadmapWs, "Roadmap");

  // ==========================================
  // SHEET 2: Grouped by Swimlane
  // ==========================================
  if (Object.keys(groupCounts).length > 0) {
    Object.entries(groupCounts).forEach(([groupName, count]) => {
      const groupData = [];

      // Group header
      groupData.push([`SWIMLANE: ${groupName}`]);
      groupData.push([`Total Entities: ${count}`]);
      groupData.push([]);

      // Column headers
      groupData.push([
        "Entity Name",
        "Lifecycle Status",
        "Plan Start",
        "Phase In",
        "Active",
        "Phase Out",
        "End of Life",
        "Successors",
        "Description",
      ]);

      // Add entities for this group
      rows.forEach((row) => {
        if (!row._isGroup && row.groupName === groupName) {
          const attrs = row.timeline_attributes || {};
          groupData.push([
            row.name || "",
            row.lifecycle_status || "Unknown",
            formatDateForExport(attrs.plan_start_date),
            formatDateForExport(attrs.phase_in_start_date),
            formatDateForExport(attrs.active_start_date),
            formatDateForExport(attrs.phase_out_start_date),
            formatDateForExport(attrs.eol_start_date),
            row.successors?.map((s) => s.name).join(", ") || "",
            row.description || "",
          ]);
        }
      });

      // Create sheet for this group (sanitize sheet name)
      const sheetName = groupName.substring(0, 28).replace(/[\\/*?[\]]/g, "_");
      const groupWs = XLSX.utils.aoa_to_sheet(groupData);
      groupWs["!cols"] = [
        { wch: 35 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 30 },
        { wch: 40 },
      ];
      XLSX.utils.book_append_sheet(wb, groupWs, sheetName);
    });
  }

  // ==========================================
  // SHEET 3: All Data (flat)
  // ==========================================
  const allData = [
    [
      "Swimlane",
      "Entity Name",
      "Lifecycle Status",
      "Plan Start",
      "Phase In",
      "Active Start",
      "Phase Out",
      "End of Life",
      "Successors",
      "Description",
    ],
  ];

  rows.forEach((row) => {
    if (!row._isGroup) {
      const attrs = row.timeline_attributes || {};
      allData.push([
        row.groupName || "(No Group)",
        row.name || "",
        row.lifecycle_status || "Unknown",
        formatDateForExport(attrs.plan_start_date),
        formatDateForExport(attrs.phase_in_start_date),
        formatDateForExport(attrs.active_start_date),
        formatDateForExport(attrs.phase_out_start_date),
        formatDateForExport(attrs.eol_start_date),
        row.successors?.map((s) => s.name).join(", ") || "",
        row.description || "",
      ]);
    }
  });

  const allDataWs = XLSX.utils.aoa_to_sheet(allData);
  allDataWs["!cols"] = [
    { wch: 35 },
    { wch: 30 },
    { wch: 15 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 30 },
    { wch: 50 },
  ];
  XLSX.utils.book_append_sheet(wb, allDataWs, "All Data");

  // ==========================================
  // SHEET 4: Summary
  // ==========================================
  const summaryData = [
    ["ROADMAP REPORT SUMMARY"],
    [""],
    ["Report Name", report?.name || "Roadmap Report"],
    ["Entity Type", data?.entity_type || "application"],
    ["Grouped By", clusterBy ? clusterBy.replace(/-/g, " ") : "None"],
    ["Total Entities", totalEntities],
    ["Total Swimlanes", Object.keys(groupCounts).length || 0],
    ["Exported At", new Date().toLocaleString()],
  ];

  // Count by status
  if (Object.keys(statusCounts).length > 0) {
    summaryData.push([""], ["ENTITIES BY LIFECYCLE STATUS"]);
    summaryData.push(["Status", "Count"]);
    Object.entries(statusCounts).forEach(([status, count]) => {
      summaryData.push([status, count]);
    });
  }

  // Count by swimlane
  if (Object.keys(groupCounts).length > 0) {
    summaryData.push([""], ["ENTITIES BY SWIMLANE"]);
    summaryData.push(["Swimlane", "Count"]);
    Object.entries(groupCounts).forEach(([group, count]) => {
      summaryData.push([group, count]);
    });
  }

  const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
  summaryWs["!cols"] = [{ wch: 40 }, { wch: 30 }];
  XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

  // Save
  const filename = sanitizeFilename(report?.name || "roadmap_report") + ".xlsx";
  XLSX.writeFile(wb, filename);
};

/**
 * Get the lifecycle status for a given time interval
 */
/**
 * Export Data Quality Report to Excel
 */
export const exportDataQualityToExcel = (report, data, _options = {}) => {
  const wb = XLSX.utils.book_new();

  const rows = data?.data?.rows || [];
  const columns = data?.columns || [];
  const summary = data?.summary || {};

  // Main data sheet
  const headers = ["Entity Name", "Quality Score (%)", "Issues"];
  columns.forEach((col) => headers.push(col.label));

  const wsData = [headers];

  rows.forEach((row) => {
    const rowData = [
      row.name || "",
      row.quality_score || 0,
      row.issues?.map((i) => `${i.attribute} (${i.type})`).join("; ") || "None",
    ];

    columns.forEach((col) => {
      const value =
        col.type === "custom"
          ? row.custom_attributes?.[col.name]
          : row.attributes?.[col.name];
      rowData.push(value || "");
    });

    wsData.push(rowData);
  });

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  const colWidths = [{ wch: 30 }, { wch: 15 }, { wch: 40 }];
  columns.forEach(() => colWidths.push({ wch: 20 }));
  ws["!cols"] = colWidths;

  XLSX.utils.book_append_sheet(wb, ws, "Data Quality");

  // Summary sheet
  const summaryData = [
    ["Data Quality Report Summary"],
    [""],
    ["Report Name", report?.name || "Data Quality Report"],
    ["Entity Type", data?.entity_type || ""],
    ["Total Entities", summary.total_entities || 0],
    ["Complete Entities", summary.complete_entities || 0],
    ["Incomplete Entities", summary.incomplete_entities || 0],
    ["Missing Required", summary.issues_by_type?.missing_required || 0],
    ["Missing Optional", summary.issues_by_type?.missing_optional || 0],
    [
      "Generated At",
      data?.generated_at ? new Date(data.generated_at).toLocaleString() : "",
    ],
    ["Exported At", new Date().toLocaleString()],
  ];

  // Attribute completeness
  if (Object.keys(summary.attribute_completeness || {}).length > 0) {
    summaryData.push([""], ["Attribute Completeness"]);
    summaryData.push(["Attribute", "Completeness %", "Filled", "Empty"]);

    Object.entries(summary.attribute_completeness).forEach(([attr, stats]) => {
      summaryData.push([
        attr.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        stats.percentage,
        stats.filled,
        stats.empty,
      ]);
    });
  }

  const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
  summaryWs["!cols"] = [{ wch: 25 }, { wch: 15 }, { wch: 10 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

  // Issues sheet
  const issuesData = [["Entity Name", "Attribute", "Issue Type", "Message"]];

  rows.forEach((row) => {
    if (row.issues && row.issues.length > 0) {
      row.issues.forEach((issue) => {
        issuesData.push([row.name, issue.attribute, issue.type, issue.message]);
      });
    }
  });

  if (issuesData.length > 1) {
    const issuesWs = XLSX.utils.aoa_to_sheet(issuesData);
    issuesWs["!cols"] = [{ wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 50 }];
    XLSX.utils.book_append_sheet(wb, issuesWs, "Issues");
  }

  // Save
  const filename =
    sanitizeFilename(report?.name || "data_quality_report") + ".xlsx";
  XLSX.writeFile(wb, filename);
};

/**
 * Export Relationship Report to Excel
 */
export const exportRelationshipToExcel = (report, data, _options = {}) => {
  const wb = XLSX.utils.book_new();

  const summary = data?.summary || {};
  const variant = data?.variant || "impact-analysis";

  // Summary sheet
  const summaryData = [
    ["Relationship Report Summary"],
    [""],
    ["Report Name", report?.name || "Relationship Report"],
    ["Variant", getVariantTitle(variant)],
    [
      "Generated At",
      data?.generated_at ? new Date(data.generated_at).toLocaleString() : "",
    ],
    ["Exported At", new Date().toLocaleString()],
    [""],
  ];

  if (variant === "impact-analysis" || variant === "dependency") {
    summaryData.push(
      ["Source Entities", summary.total_source_entities || 0],
      ["Total Impacted", summary.total_impacted || 0],
      ["Analysis Depth", data?.analysis_depth || 2],
      ["Direction", data?.analysis_direction || "both"]
    );

    if (Object.keys(summary.impacted_by_type || {}).length > 0) {
      summaryData.push([""], ["Impacts by Type"]);
      Object.entries(summary.impacted_by_type).forEach(([type, count]) => {
        summaryData.push([type, count]);
      });
    }
  } else if (variant === "matrix") {
    summaryData.push(
      ["Source Count", summary.source_count || 0],
      ["Target Count", summary.target_count || 0],
      ["Connected Cells", summary.connected_cells || 0],
      ["Coverage %", summary.coverage_percentage || 0]
    );
  }

  const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
  summaryWs["!cols"] = [{ wch: 25 }, { wch: 30 }];
  XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

  // Data sheets based on variant
  if (variant === "impact-analysis" || variant === "dependency") {
    const results = data?.data?.results || [];

    const impactData = [
      [
        "Source Entity",
        "Source Type",
        "Impacted Entity",
        "Impacted Type",
        "Depth",
        "Direction",
        "Relationship Type",
      ],
    ];

    results.forEach((result) => {
      if (result.impacts && result.impacts.length > 0) {
        result.impacts.forEach((impact) => {
          impactData.push([
            result.source.name,
            result.source.type,
            impact.name,
            impact.type,
            impact.depth,
            impact.direction,
            impact.relationship_type,
          ]);
        });
      } else {
        impactData.push([
          result.source.name,
          result.source.type,
          "No impacts",
          "",
          "",
          "",
          "",
        ]);
      }
    });

    const impactWs = XLSX.utils.aoa_to_sheet(impactData);
    impactWs["!cols"] = [
      { wch: 25 },
      { wch: 15 },
      { wch: 25 },
      { wch: 15 },
      { wch: 8 },
      { wch: 12 },
      { wch: 20 },
    ];
    XLSX.utils.book_append_sheet(wb, impactWs, "Impact Analysis");
  } else if (variant === "matrix") {
    const { sources, targets, matrix } = data?.data || {};

    if (sources && targets && matrix) {
      const matrixData = [["Source / Target", ...targets.map((t) => t.name)]];

      sources.forEach((source) => {
        const row = [source.name];
        targets.forEach((target) => {
          const cell = matrix[source.id]?.connections[target.id];
          row.push(
            cell?.connected ? (cell.count > 1 ? cell.count : "Yes") : ""
          );
        });
        matrixData.push(row);
      });

      const matrixWs = XLSX.utils.aoa_to_sheet(matrixData);
      const colWidths = [{ wch: 25 }];
      targets.forEach(() => colWidths.push({ wch: 15 }));
      matrixWs["!cols"] = colWidths;
      XLSX.utils.book_append_sheet(wb, matrixWs, "Matrix");
    }
  }

  // Save
  const filename =
    sanitizeFilename(report?.name || "relationship_report") + ".xlsx";
  XLSX.writeFile(wb, filename);
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format date for export
 */
const formatDateForExport = (dateValue) => {
  if (!dateValue) return "";
  try {
    if (typeof dateValue === "string") {
      const dateStr = dateValue.split("T")[0];
      return dateStr;
    }
    const date = new Date(dateValue);
    return date.toISOString().split("T")[0];
  } catch {
    return String(dateValue);
  }
};

/**
 * Sanitize filename for safe file saving
 */
const sanitizeFilename = (name) => {
  return name
    .replace(/[^a-z0-9_-]/gi, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .substring(0, 100);
};

/**
 * Add page numbers to PDF
 */
const addPageNumbers = (doc) => {
  const totalPages = doc.internal.getNumberOfPages();

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Page ${i} of ${totalPages}`,
      doc.internal.pageSize.getWidth() - 25,
      doc.internal.pageSize.getHeight() - 10
    );
  }
};

/**
 * Get variant title for relationship reports
 */
const getVariantTitle = (variant) => {
  switch (variant) {
    case "impact-analysis":
      return "Impact Analysis";
    case "dependency":
      return "Dependency Report";
    case "matrix":
      return "Relationship Matrix";
    default:
      return "Relationship Analysis";
  }
};

// ============================================================================
// Unified Export Interface
// ============================================================================

/**
 * Export report based on type and format
 * @param {string} reportType - 'roadmap', 'data-quality', 'relationship'
 * @param {string} format - 'pdf' or 'xlsx'
 * @param {object} report - Report metadata
 * @param {object} data - Report data from execution
 * @param {object} options - Export options
 */
